import { Button, ButtonProps, Icon, Text } from '@everlywell/ui-kit';
import { CheckCircle } from '@phosphor-icons/react';
import { useCart } from 'components/Cart';
import { isGalleri } from 'components/CheckoutQuest/CheckoutQuest.utils';
import { useOptionalPurchaseOptionsContext, useIsPurchaseOptionsEnabled } from 'components/PurchaseOptionsDrawerProvider/PurchaseOptionsDrawerProvider.context';
import { PurchaseOptionsDrawerProduct } from 'components/PurchaseOptionsDrawerProvider/PurchaseOptionsDrawerProvider.context';
import { navigate } from 'gatsby';
import React, { MouseEventHandler, useState } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { CHECKOUT_QUEST_BASE_PATH } from 'utils/constants/checkout';
import { isQuestProduct } from 'utils/helpers/products';
import { ProductDataType } from 'utils/types';

type OmittedProps =
  | 'rightIcon'
  | 'animation'
  | 'colorScheme'
  | 'size'
  | 'isDisabled'
  | '_hover';

export type AddToCartButtonProps = Omit<ButtonProps, OmittedProps> & {
  product: Pick<
    ProductDataType,
    'variantId' | 'displayName' | 'slug' | 'outOfStock'
  > & Partial<Pick<ProductDataType, 'productId' | 'price' | 'promotionable'>>;

  clickAnalyticsData: {
    label: string;
    [key: string]: any;
  };
};

/**
 * Add to cart button component that adds the product to the cart and animates the button when clicked
 */
const AddToCartButton = (props: AddToCartButtonProps) => {
  // productOverrides flag removed - defaulted to false (no overrides)
  const productOverrides: { overrides: Record<string, any> } | null = null;
  const { addItemToCart } = useCart();
  const purchaseOptionsContext = useOptionalPurchaseOptionsContext();
  const isPurchaseOptionsEnabled = useIsPurchaseOptionsEnabled();

  const { product, clickAnalyticsData, onClick, ...buttonProps } = props;
  const [isAdding, setIsAdding] = useState(false);

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    // Removed ldClient tracking - just using regular analytics

    // If the product is a Quest product, navigate to the Quest checkout page
    if (isQuestProduct(product.slug)) {
      analytics.track({
        event: ANALYTICS.EVENTS.CLICKED_BUTTON,
        data: {
          label: ANALYTICS.LABELS.QUEST_BUY_TEST,
          component: 'AddToCartButton',
          product: product.displayName,
        },
      });
      onClick?.(event);

      const variantId = product.variantId;

      if (isGalleri(product.slug)) {
        navigate(`/products/${product.slug}`);
      } else {
        navigate(`${CHECKOUT_QUEST_BASE_PATH}/?variant=${variantId}`);
      }
    } else {
      // Check if pre-cart should be shown (mobile only) and context is available
      if (isPurchaseOptionsEnabled.isEnabled && !isPurchaseOptionsEnabled.isLoading && purchaseOptionsContext) {
        // Use pre-cart drawer
        const purchaseDrawerProduct: PurchaseOptionsDrawerProduct = {
          name: product.displayName,
          slug: product.slug,
          variantId: product.variantId,
          price: product.price?.toString() ?? '0',
          subscriptionVariants: [], // Basic products don't have subscription variants in this context
          hideSubscription: false,
          preSelectedSubscription: false,
          productId: product.productId ?? 0,
          promotionable: product.promotionable ?? false,
        };

        purchaseOptionsContext.setProduct(purchaseDrawerProduct);

        analytics.track({
          event: ANALYTICS.EVENTS.CLICKED_BUTTON,
          data: {
            ...clickAnalyticsData,
            product: product.displayName,
            experience: 'Pre-Cart',
          },
        });

        onClick?.(event);
      } else {
        // Direct add to cart (legacy behavior)
        updateAddedToCartState();

        addItemToCart({
          variant_id: product.variantId,
          quantity: 1,
        });

        analytics.track({
          event: ANALYTICS.EVENTS.ADD_TO_CART,
          data: {
            ...clickAnalyticsData,
            product: product.displayName,
          },
        });

        onClick?.(event);
      }
    }
  };

  const updateAddedToCartState = () => {
    setIsAdding(true);

    // Reset the button state after 2 seconds
    // 2 seconds is the duration of the animation requested by the design team
    setTimeout(() => setIsAdding(false), 2000);
  };

  return (
    <Button
      data-testid="pip-add-to-cart-button"
      rightIcon={
        <Icon
          as={CheckCircle}
          opacity={isAdding ? 1 : 0}
          transform={isAdding ? 'translateY(0)' : 'translateY(100%)'}
          transition="all 0.5s ease-in-out"
        />
      }
      animation={isAdding ? 'transition: background 0.5s linear' : ''}
      colorScheme="greenCS"
      size="sm"
      position="relative"
      overflow="hidden"
      // Remove the onClick event if the product is being added to the cart
      onClick={isAdding ? undefined : handleClick}
      isDisabled={product.outOfStock}
      {...(!product.outOfStock && {
        _hover: {
          transition: 'all 0.3s linear',
          transform: 'scale(1.04) translateZ(0)',
          cursor: 'pointer',
        },
      })}
      minWidth={['100px', '120px']}
      height={['32px', '40px']}
      {...buttonProps}
    >
      <Text
        opacity={isAdding ? 0 : 1}
        transform={isAdding ? 'translateY(-100%)' : 'translateY(0)'}
        transition="all 0.5s ease-in-out"
        position="absolute"
      >
        Add to cart
      </Text>
    </Button>
  );
};

export default AddToCartButton;
